import { useTranslation } from 'react-i18next';
import { useCallback, useMemo } from 'react';
import { atom, useAtom } from 'jotai';

import { getTimeByKey, METRONOME_PATTERNS, NOTES } from '@/core/contants.ts';
import { NOTE, ChordPickerBar, Sequence, SIGNATURE, ProgressionOption, SelectedChord, Sample } from '@/types.ts';
import { offsetKey } from '@/core/notes.ts';

interface ProgressionState {
  key: NOTE;
  time: SIGNATURE;
  progression: SelectedChord[];
  selectedChord?: SelectedChord;
  selectedProgression: number;
  metronome: boolean;
}

const progressionAtom = atom<ProgressionState>({
  key: 'C',
  time: SIGNATURE['4/4'],
  progression: [],
  selectedProgression: 0,
  metronome: true,
});

function useProgression() {
  const [t] = useTranslation();
  const [state, setState] = useAtom(progressionAtom);

  const noteByOption = useCallback((option: ProgressionOption) => {
    return NOTES[offsetKey(state.key, option.interval)];
  }, [state.key]);

  const sampleSettingsByOption = useCallback((option: ProgressionOption, note = noteByOption(option)) => {
    return note[option.chordQuality];
  }, [noteByOption]);

  const setKey = useCallback(
    (key: string) => setState((currentState) => ({ ...currentState, key })),
    [setState],
  );

  const setTime = useCallback(
    (time: string) => setState((currentState) => ({ ...currentState, time: getTimeByKey(time) })),
    [setState],
  );

  const addChord = useCallback(
    () => setState((currentState) => {
      const newProgression = [...currentState.progression];

      if (state.selectedChord) {
        newProgression[state.selectedProgression] = state.selectedChord;
      }

      return {
        ...currentState,
        progression: newProgression,
        selectedProgression: newProgression.length,
        selectedChord: undefined,
      };
    }),
    [setState, state.selectedChord, state.selectedProgression],
  );

  const setSelectedChord = useCallback(
    (pickerIndex: number, index: number) => setState((currentState) => ({ ...currentState, selectedChord: [pickerIndex, index] })),
    [setState],
  );

  const setSelectedProgression = useCallback(
    (selectedProgression: number) => setState((currentState) => ({ ...currentState, selectedProgression })),
    [setState],
  );

  const undoProgression = useCallback(
    () => setState((currentState) => ({
      ...currentState,
      progression: currentState.progression.slice(0, -1),
      selectedProgression: currentState.progression.length - 1,
    })),
    [setState],
  );

  const toggleMetronome = useCallback(
    () => setState((currentState) => ({ ...currentState, metronome: !currentState.metronome })),
    [setState],
  );

  const progressionOptionBars = useMemo((): ChordPickerBar[] => {
    const prevSelected = state.progression[state.selectedProgression - 1];
    const nextSelected = state.progression[state.selectedProgression + 1];

    return [
      {
        name: t('progression.secondary'),
        isEnabled: (option: number) => {
          if (nextSelected) {
            if (nextSelected[0] === 1) {
              return option === nextSelected[1];
            } else {
              return nextSelected[0] !== 2;
            }
          }

          return prevSelected && prevSelected[0] === 1;
        },
        options: [
          {
            numeral: 'V/I',
            chordQuality: 'dominant',
            interval: 7,
          },
          {
            numeral: 'V/ii',
            chordQuality: 'dominant',
            interval: 9,
          },
          {
            numeral: 'V/iii',
            chordQuality: 'dominant',
            interval: 11,
          },
          {
            numeral: 'V/IV',
            chordQuality: 'dominant',
            interval: 0,
          },
          {
            numeral: 'V/V',
            chordQuality: 'dominant',
            interval: 2,
          },
          {
            numeral: 'V/vi',
            chordQuality: 'dominant',
            interval: 4,
          },
        ],
      },
      {
        name: t('progression.main'),
        isEnabled: (option: number) => {
          if (prevSelected && prevSelected[0] === 0) {
            return prevSelected[1] === option;
          }

          return true;
        },
        options: [
          {
            numeral: 'I',
            chordQuality: 'major',
            interval: 0,
          },
          {
            numeral: 'ii',
            chordQuality: 'minor',
            interval: 2,
          },
          {
            numeral: 'iii',
            chordQuality: 'minor',
            interval: 4,
          },
          {
            numeral: 'IV',
            chordQuality: 'major',
            interval: 5,
          },
          {
            numeral: 'V',
            chordQuality: 'major',
            interval: 7,
          },
          {
            numeral: 'vi',
            chordQuality: 'minor',
            interval: 9,
          },
        ],
      },
      {
        name: t('progression.interchange'),
        isEnabled: () => {
          return prevSelected && (prevSelected[0] === 1 || prevSelected[0] === 2);
        },
        options: [
          {
            numeral: 'b/III',
            chordQuality: 'major',
            interval: 3,
          },
          {
            numeral: 'b/iv',
            chordQuality: 'minor',
            interval: 5,
          },
          {
            numeral: 'b/VI',
            chordQuality: 'major',
            interval: 8,
          },
          {
            numeral: 'b/VII',
            chordQuality: 'major',
            interval: 10,
          },
        ],
      },
    ];
  }, [state.progression, state.selectedProgression, t]);

  const sequence = useMemo((): Sequence => {
    const pattern: Sample[] = state.metronome ? METRONOME_PATTERNS[state.time].map((sample) => ({
      sample,
    })) : [{ sample: 'none' }];

    const chords = state.progression.map((selectedChord) => {
      const option = progressionOptionBars[selectedChord[0]].options[selectedChord[1]];

      return sampleSettingsByOption(option);
    });

    if (chords.length > 0) {
      return chords.map((chord) => [
        pattern,
        [{ ...chord, channel: 'chord' }],
      ]);
    }

    return [
      [pattern],
    ];
  }, [
    state.time,
    state.key,
    sampleSettingsByOption,
    state.progression,
    state.metronome,
    progressionOptionBars,
  ]);

  const progressionChords = useMemo(
    (): ProgressionOption[] => state.progression
      .map((p) => progressionOptionBars[p[0]].options[p[1]]),
    [state.progression, progressionOptionBars],
  );

  return useMemo(
    () => ({
      ...state,
      setKey,
      setTime,
      setSelectedChord,
      setSelectedProgression,
      undoProgression,
      toggleMetronome,
      addChord,
      sequence,
      progressionOptionBars,
      progressionChords,
      noteByOption,
      sampleSettingsByOption,
    }),
    [
      state,
      setKey,
      setTime,
      setSelectedChord,
      setSelectedProgression,
      undoProgression,
      toggleMetronome,
      addChord,
      sequence,
      progressionOptionBars,
      progressionChords,
      noteByOption,
      sampleSettingsByOption,
    ],
  );
}

export default useProgression;
